import React from "react";
import clsx from "clsx";
import * as Icon from "react-feather";

import WComponent from "../common/WComponent";
import ToolsSidePanel from "../clients/Tools/kit/ToolsSidePanel";
import ToolsTabs from "../clients/Tools/kit/ToolsTabs";
import ToolsLedgerEntries from "../clients/Tools/kit/ToolsLedgerEntries";
import ToolsDocumentPane from "../clients/Tools/kit/ToolsDocumentPane";
import { formatDate, formatDrillAmount } from "../clients/Tools/AFSTool/shared/helpers/formatters";
import PlaybookReviewService from "../../services/PlaybookReviewService";
import PlaybookBooksService from "../../services/PlaybookBooksService";
import UtilityService from "../../services/UtilityService";

import "./agent-run-books-panel.css";

const LIVE_TAB_KEYS = ["ledger", "trialBalance", "transaction"];
const LEDGER_SPANS = [
	{ key: "month", label: "Month" },
	{ key: "yearToDate", label: "Year to date" },
];
const LEDGER_COLUMNS = ["date", "entry", "amount", "balance"];

class AgentRunBooksPanel extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			tabs: null,
			activeKey: null,
			evidence: null,
			evidenceError: null,
			isReadingEvidence: false,
			reads: {},
			ledgerSpan: "month",
			attachmentsByEntryKey: {},
			chosenAttachment: null,
		};
	}

	componentDidMount() {
		this.onChooseTab({ key: this.props.initialTab || "frozen" });
	}

	onChooseTab = async ({ key, span, isRetry }) => {
		const { runId, findingId, qboAccountId } = this.props;
		let tabs = this.state.tabs;
		if (!tabs) {
			let evidence = null;
			let evidenceError = null;
			if (findingId) {
				await this.update({ isReadingEvidence: true });
				try {
					evidence = await PlaybookReviewService.getFindingEvidence({ runId, findingId });
				} catch (error) {
					evidenceError = UtilityService.describeRequestError({ error, fallback: "The rows the agent read could not be loaded." });
				}
			}
			tabs = PlaybookBooksService.describeBooksTabs({ evidence, qboAccountId });
			await this.update({ tabs, evidence, evidenceError, isReadingEvidence: false });
		}

		const chosenTab = tabs.find(tab => tab.key === key) || tabs[0];
		const ledgerSpan = span || this.state.ledgerSpan;
		await this.update({ activeKey: chosenTab.key, ledgerSpan, chosenAttachment: null });
		if (!LIVE_TAB_KEYS.includes(chosenTab.key)) return;

		const readKey = chosenTab.key === "ledger" ? `ledger|${ledgerSpan}` : chosenTab.key;
		const earlierRead = this.state.reads[readKey];
		const hasUsableRead = !!earlierRead && !earlierRead.error;
		if (hasUsableRead && !isRetry) return;
		await this.update(previous => ({ reads: { ...previous.reads, [readKey]: { loading: true } } }));

		const isTransactionTab = chosenTab.key === "transaction";
		if (isTransactionTab && chosenTab.canListAttachments) {
			const { transactionType, transactionId } = chosenTab;
			const entryKey = PlaybookBooksService.ledgerEntryKey({ transactionType, transactionId });
			const earlierFiles = this.state.attachmentsByEntryKey[entryKey];
			const hasFailedFilesRead = !!earlierFiles && !!earlierFiles.error;
			const needsFilesRead = !earlierFiles || hasFailedFilesRead;
			if (hasFailedFilesRead) await this.onToggleAttachments({ transactionType, transactionId });
			if (needsFilesRead) await this.onToggleAttachments({ transactionType, transactionId });
		}

		try {
			let data = null;
			if (chosenTab.key === "trialBalance") data = await PlaybookBooksService.readTrialBalance({ runId });
			if (isTransactionTab && chosenTab.canReadTransaction) {
				data = await PlaybookBooksService.readTransaction({ runId, transactionType: chosenTab.transactionType, transactionId: chosenTab.transactionId });
			}
			if (chosenTab.key === "ledger") {
				const isYearToDate = ledgerSpan === "yearToDate";
				const knownTrialBalance = this.state.reads.trialBalance && this.state.reads.trialBalance.data;
				const yearOfTheRun = isYearToDate ? knownTrialBalance || (await PlaybookBooksService.readTrialBalance({ runId })) : null;
				const period = isYearToDate ? { periodStart: yearOfTheRun.periodStart, periodEnd: yearOfTheRun.periodEnd } : {};
				data = await PlaybookBooksService.readLedger({ runId, qboAccountId: chosenTab.qboAccountId, ...period });
			}
			await this.update(previous => ({ reads: { ...previous.reads, [readKey]: { data } } }));
		} catch (error) {
			const whyNot = UtilityService.describeRequestError({ error, fallback: "QuickBooks could not be read just now." });
			await this.update(previous => ({ reads: { ...previous.reads, [readKey]: { error: whyNot } } }));
		}
	};

	onToggleAttachments = async ({ transactionType, transactionId }) => {
		const { runId } = this.props;
		const entryKey = PlaybookBooksService.ledgerEntryKey({ transactionType, transactionId });
		const existing = this.state.attachmentsByEntryKey[entryKey];
		if (existing && !existing.loading) {
			const attachmentsByEntryKey = { ...this.state.attachmentsByEntryKey };
			delete attachmentsByEntryKey[entryKey];
			const isClosingTheShownFile = !!this.state.chosenAttachment && this.state.chosenAttachment.key === entryKey;
			await this.update({ attachmentsByEntryKey, chosenAttachment: isClosingTheShownFile ? null : this.state.chosenAttachment });
			return;
		}
		await this.update(previous => ({ attachmentsByEntryKey: { ...previous.attachmentsByEntryKey, [entryKey]: { loading: true, transactionType, transactionId } } }));
		try {
			const listed = await PlaybookBooksService.listAttachments({ runId, transactionType, transactionId });
			await this.update(previous => ({
				attachmentsByEntryKey: { ...previous.attachmentsByEntryKey, [entryKey]: { checked: listed.checked, attachments: listed.attachments, transactionType, transactionId } },
			}));
		} catch (error) {
			const whyNot = UtilityService.describeRequestError({ error, fallback: "QuickBooks's attachments could not be read." });
			await this.update(previous => ({ attachmentsByEntryKey: { ...previous.attachmentsByEntryKey, [entryKey]: { error: whyNot, transactionType, transactionId } } }));
		}
	};

	renderFrozen() {
		const { evidence, evidenceError, isReadingEvidence } = this.state;
		if (isReadingEvidence) return <p className="agent-run-books-panel__message">Loading the rows the agent read…</p>;
		if (evidenceError) return <p className="agent-run-books-panel__message agent-run-books-panel__message--problem">{evidenceError}</p>;
		if (!evidence) return <p className="agent-run-books-panel__message">This card cites no rows the agent read.</p>;
		const { qboAccountId } = this.props;
		const cardAccount = { qboAccountId, name: null };
		const { tone, summary } = PlaybookReviewService.describeProof({ proof: evidence.proof, cardAccount });
		const describedSteps = evidence.steps.map(step => PlaybookReviewService.describeFrozenStep({ step }));

		return (
			<div className="agent-run-books-panel__frozen" data-test="agent-run-books-frozen">
				<p className={clsx("agent-run-books-panel__proof", `agent-run-books-panel__proof--${tone}`)}>
					{tone === "verified" && <Icon.CheckCircle size={14} className="agent-run-books-panel__proof-icon" />}
					{tone === "failing" && <Icon.AlertCircle size={14} className="agent-run-books-panel__proof-icon" />}
					{summary}
				</p>
				<p className="agent-run-books-panel__frozen-note">As the agent read them during the run. Nothing here changes when QuickBooks does.</p>
				{describedSteps.map(step => (
					<section key={step.key} className="agent-run-books-panel__step">
						<div className="agent-run-books-panel__step-head">
							<span className="agent-run-books-panel__step-title">{step.title}</span>
							{step.periodText && <span className="agent-run-books-panel__step-period">{step.periodText}</span>}
						</div>
						{step.truncatedNote && <p className="agent-run-books-panel__step-note">{step.truncatedNote}</p>}
						<div className="agent-run-books-panel__rows">
							{step.rows.map(row => (
								<div key={row.key} className={clsx("agent-run-books-panel__row", row.isCited && "agent-run-books-panel__row--cited")}>
									<span className="agent-run-books-panel__row-date">{row.date}</span>
									<span className="agent-run-books-panel__row-text" title={row.detail ? `${row.text} · ${row.detail}` : row.text}>
										{row.text}
										{row.detail && <span className="agent-run-books-panel__row-detail">{row.detail}</span>}
									</span>
									<span className="agent-run-books-panel__row-amount">{row.amount}</span>
								</div>
							))}
						</div>
					</section>
				))}
			</div>
		);
	}

	renderTrialBalance({ tab, trialBalance }) {
		const highlightedAccountId = tab.qboAccountId ? String(tab.qboAccountId) : null;

		return (
			<div className="agent-run-books-panel__trial-balance" data-test="agent-run-books-trial-balance">
				<p className="agent-run-books-panel__period">
					{formatDate(trialBalance.periodStart)} to {formatDate(trialBalance.periodEnd)}
				</p>
				<div className="agent-run-books-panel__tb-row agent-run-books-panel__tb-row--head">
					<span>Account</span>
					<span className="agent-run-books-panel__tb-amount">Debit</span>
					<span className="agent-run-books-panel__tb-amount">Credit</span>
				</div>
				{trialBalance.rows.map(row => (
					<div
						key={row.qboAccountId}
						className={clsx("agent-run-books-panel__tb-row", String(row.qboAccountId) === highlightedAccountId && "agent-run-books-panel__tb-row--highlighted")}
					>
						<span className="agent-run-books-panel__tb-name" title={row.name}>{row.name}</span>
						<span className="agent-run-books-panel__tb-amount">{row.debitCents ? formatDrillAmount(row.debitCents / 100) : ""}</span>
						<span className="agent-run-books-panel__tb-amount">{row.creditCents ? formatDrillAmount(row.creditCents / 100) : ""}</span>
					</div>
				))}
			</div>
		);
	}

	renderTransaction({ tab, transaction }) {
		const { runId } = this.props;
		const { attachmentsByEntryKey, chosenAttachment } = this.state;
		const entryKey = PlaybookBooksService.ledgerEntryKey({ transactionType: tab.transactionType, transactionId: tab.transactionId });
		const attachments = attachmentsByEntryKey[entryKey];
		const isShowingAFile = !!chosenAttachment && chosenAttachment.key === entryKey;
		const shownFileUrl = isShowingAFile
			? PlaybookBooksService.attachmentViewUrl({ runId, transactionType: tab.transactionType, transactionId: tab.transactionId, attachableId: chosenAttachment.attachment.id })
			: null;

		return (
			<div className="agent-run-books-panel__transaction" data-test="agent-run-books-transaction">
				{transaction && (
					<div className="agent-run-books-panel__txn-head">
						<span className="agent-run-books-panel__txn-title">
							{[transaction.transactionType, transaction.number].filter(Boolean).join(" ")}
							{transaction.payee && <span className="agent-run-books-panel__txn-payee">{transaction.payee}</span>}
						</span>
						<span className="agent-run-books-panel__txn-total">{formatDrillAmount(transaction.total)}</span>
						<span className="agent-run-books-panel__txn-meta">{formatDate(transaction.date)}</span>
						{transaction.memo && <span className="agent-run-books-panel__txn-memo">{transaction.memo}</span>}
					</div>
				)}
				{transaction && (
					<div className="agent-run-books-panel__txn-lines">
						{transaction.lines.map((line, position) => (
							<div key={position} className="agent-run-books-panel__txn-line">
								<span className="agent-run-books-panel__txn-line-name">
									{line.account ? line.account.name : line.item.name}
									{line.description && <span className="agent-run-books-panel__txn-line-description">{line.description}</span>}
								</span>
								<span className="agent-run-books-panel__txn-line-posting">{line.postingType || ""}</span>
								<span className="agent-run-books-panel__txn-line-amount">{formatDrillAmount(line.amount)}</span>
							</div>
						))}
					</div>
				)}
				{tab.canListAttachments && (
					<div className="agent-run-books-panel__txn-files">
						<span className="agent-run-books-panel__section-label">Attachments</span>
						{(!attachments || attachments.loading) && <p className="agent-run-books-panel__files-note">Checking QuickBooks for attachments…</p>}
						{attachments && attachments.error && <p className="agent-run-books-panel__files-note agent-run-books-panel__files-note--problem">{attachments.error}</p>}
						{attachments && !attachments.loading && !attachments.error && !attachments.checked && (
							<p className="agent-run-books-panel__files-note">Attachments cannot be checked for this kind of transaction yet.</p>
						)}
						{attachments && attachments.checked && attachments.attachments.length === 0 && <p className="agent-run-books-panel__files-note">No attachments on this transaction.</p>}
						{attachments && attachments.checked && attachments.attachments.length > 0 && (
							<div className="agent-run-books-panel__files">
								{attachments.attachments.map(attachment => (
									<button
										key={attachment.id}
										type="button"
										className={clsx("agent-run-books-panel__file", isShowingAFile && chosenAttachment.attachment.id === attachment.id && "agent-run-books-panel__file--chosen")}
										onClick={() => this.update({ chosenAttachment: { key: entryKey, attachment } })}
									>
										<Icon.Paperclip size={12} /> {attachment.fileName}
									</button>
								))}
							</div>
						)}
					</div>
				)}
				{isShowingAFile && (
					<div className="agent-run-books-panel__document">
						<ToolsDocumentPane title={chosenAttachment.attachment.fileName} url={shownFileUrl} withOpenLink />
					</div>
				)}
			</div>
		);
	}

	renderLedger({ tab, ledger }) {
		const { runId } = this.props;
		const { ledgerSpan, attachmentsByEntryKey, chosenAttachment } = this.state;
		const ledgerChanges = ledger ? ledger.changesSinceRun : [];
		const citedEntryKeys = tab.citedEntryKeys || [];
		const isACitedLineShown =
			!!ledger && ledger.entries.some(entry => citedEntryKeys.includes(PlaybookBooksService.ledgerEntryKey({ transactionType: entry.transactionType, transactionId: entry.transactionId })));
		const shownFile =chosenAttachment ? attachmentsByEntryKey[chosenAttachment.key] : null;
		const shownFileUrl = shownFile
			? PlaybookBooksService.attachmentViewUrl({ runId, transactionType: shownFile.transactionType, transactionId: shownFile.transactionId, attachableId: chosenAttachment.attachment.id })
			: null;

		return (
			<div className="agent-run-books-panel__ledger" data-test="agent-run-books-ledger">
				<div className="agent-run-books-panel__span">
					<ToolsTabs segmented tabs={LEDGER_SPANS} activeKey={ledgerSpan} onSelect={span => this.onChooseTab({ key: "ledger", span })} />
					{ledger && (
						<span className="agent-run-books-panel__period">
							{formatDate(ledger.periodStart)} to {formatDate(ledger.periodEnd)}
						</span>
					)}
				</div>
				{ledger && ledgerChanges.length > 0 && (
					<div className="agent-run-books-panel__changes" data-test="agent-run-books-changes">
						<span className="agent-run-books-panel__changes-title">
							<Icon.AlertTriangle size={13} /> Changed in QuickBooks since the run
						</span>
						{ledgerChanges.map((change, position) => (
							<span key={position} className="agent-run-books-panel__change">{PlaybookBooksService.describeChangeSinceRun({ change })}</span>
						))}
					</div>
				)}
				{ledger && ledgerChanges.length === 0 && isACitedLineShown && (
					<p className="agent-run-books-panel__unchanged">
						<Icon.Check size={13} /> The lines the agent cited are unchanged since the run.
					</p>
				)}
				{ledger && (
					<ToolsLedgerEntries
						ledger={ledger}
						columns={LEDGER_COLUMNS}
						highlightedEntryKeys={citedEntryKeys}
						attachmentsByEntryKey={attachmentsByEntryKey}
						onToggleAttachments={this.onToggleAttachments}
						onPreviewAttachment={({ attachment, key }) => this.update({ chosenAttachment: { key, attachment } })}
					/>
				)}
				{shownFileUrl && (
					<div className="agent-run-books-panel__document">
						<ToolsDocumentPane title={chosenAttachment.attachment.fileName} url={shownFileUrl} withOpenLink />
					</div>
				)}
			</div>
		);
	}

	renderDocument({ tab }) {
		return (
			<div className="agent-run-books-panel__drive-file" data-test="agent-run-books-document">
				<Icon.FileText size={16} className="agent-run-books-panel__drive-file-icon" />
				<span className="agent-run-books-panel__drive-file-name" title={tab.fileName}>{tab.fileName}</span>
				{tab.driveUrl && (
					<a className="agent-run-books-panel__drive-file-open" href={tab.driveUrl} target="_blank" rel="noopener noreferrer">
						<Icon.ExternalLink size={12} /> Open in Google Drive
					</a>
				)}
			</div>
		);
	}

	render() {
		const { title, onClose } = this.props;
		const { tabs, activeKey, reads, ledgerSpan } = this.state;
		const activeTab = tabs ? tabs.find(tab => tab.key === activeKey) : null;
		const isLiveTab = !!activeTab && LIVE_TAB_KEYS.includes(activeTab.key);
		const readKey = activeTab && activeTab.key === "ledger" ? `ledger|${ledgerSpan}` : activeKey;
		const liveRead = isLiveTab ? reads[readKey] : null;
		const liveData = liveRead && liveRead.data ? liveRead.data : null;
		const isAttachmentsOnly = !!activeTab && activeTab.key === "transaction" && !activeTab.canReadTransaction;
		const hasLiveFigures = !!liveData || (isAttachmentsOnly && !!liveRead && !liveRead.loading && !liveRead.error);

		return (
			<ToolsSidePanel isOpen title={title} onClose={onClose}>
				<div className="tk-side-panel__body agent-run-books-panel" data-test="agent-run-books-panel">
					{tabs && tabs.length > 1 && (
						<div className="agent-run-books-panel__tabs">
							<ToolsTabs tabs={tabs} activeKey={activeKey} onSelect={key => this.onChooseTab({ key })} />
						</div>
					)}
					{!activeTab && <p className="agent-run-books-panel__message">Loading…</p>}
					{activeKey === "frozen" && this.renderFrozen()}

					{isLiveTab && (
						<div className="agent-run-books-panel__live-bar">
							<span className="agent-run-books-panel__as-of">
								<Icon.Radio size={12} />
								{liveData && liveData.asOf ? PlaybookBooksService.describeAsOf({ asOf: liveData.asOf }) : "From QuickBooks, now"}
							</span>
							{liveRead && !liveRead.loading && (
								<button type="button" className="tk-text-action tk-text-action--xs" onClick={() => this.onChooseTab({ key: activeKey, isRetry: true })}>
									<Icon.RefreshCw size={11} /> Read again
								</button>
							)}
						</div>
					)}
					{isLiveTab && (!liveRead || liveRead.loading) && <p className="agent-run-books-panel__message">Reading QuickBooks…</p>}
					{isLiveTab && liveRead && liveRead.error && (
						<div className="agent-run-books-panel__message agent-run-books-panel__message--problem">
							<Icon.AlertTriangle size={14} />
							<span>{liveRead.error}</span>
						</div>
					)}

					{activeKey === "ledger" && this.renderLedger({ tab: activeTab, ledger: liveData })}
					{activeKey === "trialBalance" && liveData && this.renderTrialBalance({ tab: activeTab, trialBalance: liveData })}
					{activeKey === "transaction" && hasLiveFigures && this.renderTransaction({ tab: activeTab, transaction: liveData })}
					{activeKey === "document" && this.renderDocument({ tab: activeTab })}
				</div>
			</ToolsSidePanel>
		);
	}
}

export default AgentRunBooksPanel;
